import { Stack, SxProps } from "@mui/material";
import { Theme } from "@mui/material/styles";

import AppBreadcrumbs from "@/common/components/AppBreadcrumbs";
import { EntityViewVariant, IBasePageLayout, ViewVariant } from "@/common/ts/layout";

const defaultDisplayProps = {
  breadcrumbs: true,
  viewVariant: ViewVariant.Page,
  entityViewVariant: EntityViewVariant.View,
};

export interface ViewLayoutProps extends IBasePageLayout {
  sx?: SxProps<Theme>;
  displayProps?: Partial<typeof defaultDisplayProps>;
}

/** View, ListView layout v2. */
export function ViewLayout({
  breadcrumbs,
  header,
  body,
  footer,
  sx,
  displayProps,
  children,
}: ViewLayoutProps) {
  const bodyComputed = children || body;
  const displayPropsComputed = { ...defaultDisplayProps, ...displayProps };

  const isEntityList = displayProps?.entityViewVariant === EntityViewVariant.List;
  const isEntityView = displayProps?.entityViewVariant === EntityViewVariant.View;

  return (
    <>
      {/* Note: ensure content is stretched to full height of a parent. */}
      {displayPropsComputed?.viewVariant === ViewVariant.Page && (
        <Stack
          data-testid='ViewLayout'
          data-view-variant={displayPropsComputed?.viewVariant}
          data-entity-view-variant={displayPropsComputed?.entityViewVariant}
          className='AppViewLayout'
          direction='column'
          sx={{ minHeight: "100%", pb: 10, gap: 1.25, ...sx }}
        >
          <Stack
            data-testid='ViewLayout-HeaderWrapper'
            className='ViewLayoutV2HeaderWrapper'
            sx={{
              px: { xxs: 1, md: 2 },
              pt: 1.25,
              pb: 0,
              gap: 1.25,
              background: "none",
              ...(isEntityList ? { pt: 1.25, pb: 1.25 } : {}),
              ...(isEntityView ? { pt: 1.25, pb: 0 } : {}),
            }}
          >
            {displayPropsComputed?.breadcrumbs && (
              <AppBreadcrumbs replacements={breadcrumbs?.replacements} />
            )}

            {!!header && header}
          </Stack>

          <Stack
            data-testid='ViewLayout-ContentWrapper'
            className='ViewLayoutV2ContentWrapper'
            sx={{
              flex: 1,
              px: { xxs: 1, md: 2 },
              borderRadius: "10px",
            }}
            gap={1.25}
          >
            {bodyComputed && bodyComputed}

            {footer && footer}
          </Stack>
        </Stack>
      )}

      {displayPropsComputed?.viewVariant === ViewVariant.Tab && (
        <Stack
          data-testid='ViewLayout'
          data-view-variant={displayPropsComputed?.viewVariant}
          data-entity-view-variant={displayPropsComputed?.entityViewVariant}
          className='AppViewLayout'
          direction='column'
          sx={{ height: "100%", ...sx }}
        >
          {header && (
            <Stack
              className='ViewLayoutV2HeaderWrapper'
              sx={{
                p: 1.25,
                backgroundColor: (t) => t.palette.background.paper,
              }}
            >
              {header}
            </Stack>
          )}

          <Stack
            className='ViewLayoutV2ContentWrapper'
            sx={{
              gap: 1.25,
              border: "none",
              borderBottomLeftRadius: "10px",
              borderBottomRightRadius: "10px",
            }}
          >
            {bodyComputed && bodyComputed}
            {footer && footer}
          </Stack>
        </Stack>
      )}

      {displayPropsComputed?.viewVariant === ViewVariant.Modal && (
        <Stack
          data-testid='ViewLayout'
          data-view-variant={displayPropsComputed?.viewVariant}
          data-entity-view-variant={displayPropsComputed?.entityViewVariant}
          className='AppViewLayout'
          direction='column'
          sx={{ gap: 1.25, ...sx }}
        >
          {header && (
            <Stack
              className='ViewLayoutV2HeaderWrapper'
              sx={{
                backgroundColor: (t) => t.palette.background.paper,
              }}
            >
              {header}
            </Stack>
          )}

          {bodyComputed}

          {footer && footer}
        </Stack>
      )}
    </>
  );
}
